import React, { useState, useEffect, useRef } from 'react';
import { addDoc, setDoc, doc, collection, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import SecureInput from '../common/SecureInput';
import { sanitizeString } from '../../utils/sanitization';
import { logEntityChanges, logUserAction } from '../../utils/logger';
import { collectionPath } from '../../utils/firestoreCollections';
import { usePermissions } from '../../hooks/usePermissions';
import { FEATURE_PERMISSIONS, DEFAULT_ROLE_PERMISSIONS } from '../../utils/permissions';

const UserModal = ({ user, open, onClose, initial }) => {
    const { can } = usePermissions(user);
    const [firstName, setFirstName] = useState(initial?.firstName || '');
    const [lastName, setLastName] = useState(initial?.lastName || '');
    const [email, setEmail] = useState(initial?.email || '');
    const [phone, setPhone] = useState(initial?.phone || '');
    const [address1, setAddress1] = useState(initial?.address1 || '');
    const [address2, setAddress2] = useState(initial?.address2 || '');
    const [city, setCity] = useState(initial?.city || '');
    const [state, setState] = useState(initial?.state || '');
    const [zip, setZip] = useState(initial?.zip || '');
    const [country, setCountry] = useState(initial?.country || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [role, setRole] = useState(initial?.role ?? ''); // Added state for role
    const [displayName, setDisplayName] = useState(initial?.displayName || '');
    const [availableRoles, setAvailableRoles] = useState([]);
    const [managerId, setManagerId] = useState(initial?.managerId || '');
    const [managerName, setManagerName] = useState(initial?.managerName || '');
    const [availableManagers, setAvailableManagers] = useState([]);
    const [title, setTitle] = useState(initial?.title || '');
    const [titleCustom, setTitleCustom] = useState(false);
    const [department, setDepartment] = useState(initial?.department || '');
    const [departmentCustom, setDepartmentCustom] = useState(false);
    const [hideFromReports, setHideFromReports] = useState(initial?.hideFromReports || false);
    // Territories: array of territory names assigned to this user
    const [territories, setTerritories] = useState(Array.isArray(initial?.territories) ? initial.territories : []);
    const [availableTerritories, setAvailableTerritories] = useState([]);
    const [territoryDropdownOpen, setTerritoryDropdownOpen] = useState(false);
    const [newTerritoryName, setNewTerritoryName] = useState('');
    const [creatingTerritory, setCreatingTerritory] = useState(false);
    const territoryDropdownRef = useRef(null);

    // Load roles from Firestore and defaults
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const rolesSnap = await getDocs(collection(db, collectionPath('rolePermissions')));
                const roleDisplayNames = {
                    admin: 'Admin',
                    adminViewOnly: 'Admin - View Only',
                    manager: 'Manager',
                    marketing: 'Marketing',
                    user: 'User',
                    disabled: 'Disabled',
                };
                const getRoleName = (id) => roleDisplayNames[id] || id.charAt(0).toUpperCase() + id.slice(1);

                const firestoreRoles = rolesSnap.docs.map(doc => ({
                    id: doc.id,
                    name: doc.data().name || getRoleName(doc.id)
                }));
                
                // Add default roles if not in Firestore
                const existingIds = firestoreRoles.map(r => r.id);
                Object.keys(DEFAULT_ROLE_PERMISSIONS).forEach(roleId => {
                    if (!existingIds.includes(roleId)) {
                        firestoreRoles.push({
                            id: roleId,
                            name: getRoleName(roleId)
                        });
                    }
                });
                
                setAvailableRoles(firestoreRoles.sort((a, b) => a.name.localeCompare(b.name)));
            } catch (error) {
                console.error('Error fetching roles:', error);
                const roleDisplayNames2 = {
                    admin: 'Admin',
                    adminViewOnly: 'Admin - View Only',
                    manager: 'Manager',
                    marketing: 'Marketing',
                    user: 'User',
                    disabled: 'Disabled',
                };
                // Fallback to default roles
                setAvailableRoles(Object.keys(DEFAULT_ROLE_PERMISSIONS).map(roleId => ({
                    id: roleId,
                    name: roleDisplayNames2[roleId] || roleId.charAt(0).toUpperCase() + roleId.slice(1)
                })));
            }
        };
        fetchRoles();
    }, []);

    // Load managers (users with role 'manager' or 'admin')
    useEffect(() => {
        const fetchManagers = async () => {
            try {
                const usersSnap = await getDocs(collection(db, collectionPath('users')));
                const managers = usersSnap.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(u => {
                        // Exclude the current user being edited
                        if (initial && u.id === initial.id) return false;
                        // Include admin, manager, and adminViewOnly roles as potential managers
                        return u.role === 'manager' || u.role === 'admin' || u.role === 'adminViewOnly';
                    })
                    .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
                
                setAvailableManagers(managers);
            } catch (error) {
                console.error('Error fetching managers:', error);
                setAvailableManagers([]);
            }
        };
        fetchManagers();
    }, [initial]);

    // Load available territories from Firestore
    useEffect(() => {
        if (!open) return;
        const fetchTerritories = async () => {
            try {
                const snap = await getDocs(collection(db, collectionPath('territories')));
                const list = snap.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .map(t => (t.name || '').trim())
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b));
                // De-duplicate (in case of case variations)
                setAvailableTerritories(Array.from(new Set(list)));
            } catch (err) {
                console.error('Error fetching territories:', err);
                setAvailableTerritories([]);
            }
        };
        fetchTerritories();
    }, [open]);

    // Close territory dropdown on outside click
    useEffect(() => {
        if (!territoryDropdownOpen) return;
        const handleClickOutside = (e) => {
            if (territoryDropdownRef.current && !territoryDropdownRef.current.contains(e.target)) {
                setTerritoryDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [territoryDropdownOpen]);

    useEffect(() => {
        if (initial) {
            setFirstName(initial.firstName || '');
            setLastName(initial.lastName || '');
            setEmail(initial.email || '');
            setPhone(initial.phone || '');
            setAddress1(initial.address1 || '');
            setAddress2(initial.address2 || '');
            setCity(initial.city || '');
            setState(initial.state || '');
            setZip(initial.zip || '');
            setCountry(initial.country || '');
            setRole(initial.role ?? ''); // Set role from initial data
            setDisplayName(initial.displayName || '');
            setManagerId(initial.managerId || '');
            setManagerName(initial.managerName || '');
            setTitle(initial.title || '');
            setTitleCustom(!!initial.title && !['Sales Rep','Sales Manager','IT Support','Marketing','Executive','Shipping','Accountant'].includes(initial.title));
            setDepartment(initial.department || '');
            setDepartmentCustom(!!initial.department && !['Sales','Marketing','HR','IT','Logistics','Finance','Customer Service','Executive'].includes(initial.department));
            setHideFromReports(initial.hideFromReports || false);
            setTerritories(Array.isArray(initial.territories) ? initial.territories : []);
        } else {
            setFirstName('');
            setLastName('');
            setEmail('');
            setPhone('');
            setAddress1('');
            setAddress2('');
            setCity('');
            setState('');
            setZip('');
            setCountry('');
            setRole(''); // Reset role when adding new
            setDisplayName('');
            setManagerId('');
            setManagerName('');
            setTitle('');
            setTitleCustom(false);
            setDepartment('');
            setDepartmentCustom(false);
            setHideFromReports(false);
            setTerritories([]);
        }
    }, [initial, open]);

    // Add duplicate email check before saving
    // Toggle a territory assignment for this user
    const handleToggleTerritory = (name) => {
        if (!can(FEATURE_PERMISSIONS.USER_MANAGE_TERRITORIES)) return;
        const clean = (name || '').trim();
        if (!clean) return;
        setTerritories(prev => {
            const has = prev.some(t => (t || '').toLowerCase() === clean.toLowerCase());
            if (has) return prev.filter(t => (t || '').toLowerCase() !== clean.toLowerCase());
            return [...prev, clean];
        });
    };

    // Create a new territory in Firestore (if it doesn't exist) and assign it to this user
    const handleCreateTerritory = async () => {
        if (!can(FEATURE_PERMISSIONS.USER_MANAGE_TERRITORIES)) return;
        const clean = (newTerritoryName || '').trim();
        if (!clean) return;
        // If it already exists in availableTerritories (case-insensitive), just assign it
        const existing = availableTerritories.find(t => t.toLowerCase() === clean.toLowerCase());
        if (existing) {
            if (!territories.some(t => (t || '').toLowerCase() === existing.toLowerCase())) {
                setTerritories(prev => [...prev, existing]);
            }
            setNewTerritoryName('');
            return;
        }
        try {
            setCreatingTerritory(true);
            const sanitized = sanitizeString(clean, 100);
            const territoryRef = await addDoc(collection(db, collectionPath('territories')), {
                name: sanitized,
                createdAt: serverTimestamp(),
                createdBy: user?.uid || null,
                createdByName: user?.displayName || user?.email || null,
            });
            setAvailableTerritories(prev => Array.from(new Set([...prev, sanitized])).sort((a, b) => a.localeCompare(b)));
            setTerritories(prev => prev.some(t => (t || '').toLowerCase() === sanitized.toLowerCase()) ? prev : [...prev, sanitized]);
            setNewTerritoryName('');
            // Audit log: territory created
            try {
                await logUserAction({
                    action: 'Territory Created',
                    details: `Created territory "${sanitized}"`,
                    entityType: 'Territory',
                    entityId: territoryRef.id,
                });
            } catch (logErr) {
                console.warn('Failed to log territory creation:', logErr);
            }
        } catch (err) {
            console.error('Failed to create territory:', err);
            setError('Failed to create territory');
        } finally {
            setCreatingTerritory(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!can(FEATURE_PERMISSIONS.USER_EDIT)) return;

        if (!firstName.trim()) {
            setError('First Name is required');
            return;
        }
        if (!lastName.trim()) {
            setError('Last Name is required');
            return;
        }
        if (!email.trim()) {
            setError('Email is required');
            return;
        }
        if (!phone.trim()) {
            setError('Phone Number is required');
            return;
        }
        if (!role) {
            setError('Role is required');
            return;
        }

        // If role changed TO disabled, trigger the full deactivation flow instead of a normal save
        if (role === 'disabled' && initial?.id && initial.role !== 'disabled') {
            onClose({ deactivate: true, id: initial.id, email: initial.email, uid: initial.uid || initial.id });
            return;
        }

        // Check for duplicate email
        try {
            const usersSnap = await getDocs(collection(db, collectionPath('users')));
            const emailLower = email.trim().toLowerCase();
            const duplicate = usersSnap.docs.find(doc => {
                const data = doc.data();
                // Exclude current user when editing
                if (initial && initial.id === doc.id) return false;
                return (data.email || '').trim().toLowerCase() === emailLower;
            });
            if (duplicate) {
                setError('A user with this email already exists.');
                return;
            }
        } catch (err) {
            setError('Failed to check for duplicate email.');
            return;
        }

        setSaving(true);
        const data = {
            firstName: sanitizeString(firstName, 100),
            lastName: sanitizeString(lastName, 100),
            full_name: `${sanitizeString(firstName, 100).trim()} ${sanitizeString(lastName, 100).trim()}`.trim(),
            displayName: sanitizeString(displayName, 100),
            email: sanitizeString(email, 200),
            phone: sanitizeString(phone, 50),
            address1: sanitizeString(address1, 200),
            address2: sanitizeString(address2, 200),
            city: sanitizeString(city, 100),
            state: sanitizeString(state, 100),
            zip: sanitizeString(zip, 20),
            country: sanitizeString(country, 100),
            role,
            hideFromReports: hideFromReports || false,
            managerId: managerId || null,
            managerName: managerName || null,
            title: title ? title.replace(/\b\w/g, c => c.toUpperCase()) : null,
            department: department ? department.replace(/\b\w/g, c => c.toUpperCase()) : null,
            territories: Array.isArray(territories)
                ? Array.from(new Set(territories.map(t => (t || '').trim()).filter(Boolean)))
                : []
        };
        const mergedData = { ...initial, ...data };
        try {
            let logEntry;
            if (initial && initial.id) {
                await setDoc(doc(db, collectionPath('users'), initial.id), data, { merge: true });
                logEntry = await logEntityChanges({
                    oldObj: initial,
                    newObj: mergedData,
                    fields: [
                        'firstName', 'lastName', 'email', 'phone', 'address1', 'address2',
                        'city', 'state', 'zip', 'country', 'managerId', 'managerName',
                        'title', 'department', 'role', 'hideFromReports', 'territories'
                    ],
                    entityType: 'User',
                    entityId: initial.id,
                    user,
                    action: 'Updated User Info'
                });
                setSaving(false);
                onClose({ ...initial, ...data, id: initial.id }, logEntry);
            } else {
                const docRef = await addDoc(collection(db, collectionPath('users')), data);
                logEntry = await logEntityChanges({
                    oldObj: {},
                    newObj: data,
                    fields: [
                        'firstName', 'lastName', 'email', 'phone', 'address1', 'address2',
                        'city', 'state', 'zip', 'country', 'managerId', 'managerName',
                        'title', 'department', 'role', 'hideFromReports', 'territories'
                    ],
                    entityType: 'User',
                    entityId: docRef.id,
                    user,
                    action: 'Created User'
                });
                setSaving(false);
                onClose({ ...data, id: docRef.id }, logEntry);
            }
        } catch (err) {
            console.error('Save user error:', err);
            setError('Failed to save user');
            setSaving(false);
        }
    };

    // Allow if admin OR editing self
    const canEdit =
        user &&
        open &&
        (can(FEATURE_PERMISSIONS.USER_VIEW_ALL) || (initial && user.uid === initial.id));

    if (!canEdit) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40">
            <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-lg w-full max-w-2xl space-y-4">
                <h3 className="text-lg font-semibold">{initial ? 'Edit User' : 'Add User'}</h3>
                {error && <div className="text-red-500">{error}</div>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">First Name<span className="text-red-600">*</span></label>
                            <SecureInput
                                className="w-full border p-2 rounded"
                                placeholder="First Name"
                                value={firstName}
                                onChange={setFirstName}
                                maxLength={100}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Last Name<span className="text-red-600">*</span></label>
                            <SecureInput
                                className="w-full border p-2 rounded"
                                placeholder="Last Name"
                                value={lastName}
                                onChange={setLastName}
                                maxLength={100}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Email<span className="text-red-600">*</span></label>
                            <SecureInput
                                className="w-full border p-2 rounded"
                                placeholder="Email"
                                type="email"
                                value={email}
                                onChange={setEmail}
                                maxLength={100}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Phone<span className="text-red-600">*</span></label>
                            <input
                                className="w-full border p-2 rounded"
                                placeholder="Phone"
                                value={phone}
                                onChange={e => setPhone(e.target.value)}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Display Name</label>
                            <input
                                className="w-full border p-2 rounded bg-gray-50"
                                placeholder="Display Name"
                                value={displayName}
                                onChange={e => setDisplayName(e.target.value)}
                                maxLength={100}
                                required
                                disabled={!can(FEATURE_PERMISSIONS.USER_MANAGE_ATTRIBUTES)}
                                style={!can(FEATURE_PERMISSIONS.USER_MANAGE_ATTRIBUTES)
                                    ? { opacity: 0.6, cursor: 'not-allowed' }
                                    : {}
                                }
                            />
                        </div>
                    </div>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Address 1</label>
                            <input className="w-full border p-2 rounded" placeholder="Address 1" value={address1} onChange={e => setAddress1(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Address 2</label>
                            <input className="w-full border p-2 rounded" placeholder="Address 2" value={address2} onChange={e => setAddress2(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">City</label>
                            <input className="w-full border p-2 rounded" placeholder="City" value={city} onChange={e => setCity(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">State</label>
                            <input className="w-full border p-2 rounded" placeholder="State" value={state} onChange={e => setState(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Zip</label>
                            <input className="w-full border p-2 rounded" placeholder="Zip" value={zip} onChange={e => setZip(e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Country</label>
                            <input className="w-full border p-2 rounded" placeholder="Country" value={country} onChange={e => setCountry(e.target.value)} />
                        </div>
                    </div>
                </div>
                {/* Title and Department dropdowns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Title
                            <span className="text-gray-500 text-xs ml-2">(Optional)</span>
                        </label>
                        <select
                            className="w-full border p-2 rounded bg-gray-50"
                            value={titleCustom ? '__custom__' : title}
                            onChange={e => {
                                if (e.target.value === '__custom__') {
                                    setTitleCustom(true);
                                    setTitle('');
                                } else {
                                    setTitleCustom(false);
                                    setTitle(e.target.value);
                                }
                            }}
                            disabled={!can(FEATURE_PERMISSIONS.USER_MANAGE_ATTRIBUTES)}
                            style={!can(FEATURE_PERMISSIONS.USER_MANAGE_ATTRIBUTES) ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                        >
                            <option value="">Select Title</option>
                            <option value="Sales Rep">Sales Rep</option>
                            <option value="Sales Manager">Sales Manager</option>
                            <option value="IT Support">IT Support</option>
                            <option value="Marketing">Marketing</option>
                            <option value="Executive">Executive</option>
                            <option value="Shipping">Shipping</option>
                            <option value="Accountant">Accountant</option>
                            <option value="__custom__">Custom...</option>
                        </select>
                        {titleCustom && (
                            <input
                                type="text"
                                className="w-full border p-2 rounded bg-gray-50 mt-2"
                                placeholder="Enter custom title"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                disabled={!can(FEATURE_PERMISSIONS.USER_MANAGE_ATTRIBUTES)}
                                autoFocus
                            />
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Department
                            <span className="text-gray-500 text-xs ml-2">(Optional)</span>
                        </label>
                        <select
                            className="w-full border p-2 rounded bg-gray-50"
                            value={departmentCustom ? '__custom__' : department}
                            onChange={e => {
                                if (e.target.value === '__custom__') {
                                    setDepartmentCustom(true);
                                    setDepartment('');
                                } else {
                                    setDepartmentCustom(false);
                                    setDepartment(e.target.value);
                                }
                            }}
                            disabled={!can(FEATURE_PERMISSIONS.USER_MANAGE_ATTRIBUTES)}
                            style={!can(FEATURE_PERMISSIONS.USER_MANAGE_ATTRIBUTES) ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                        >
                            <option value="">Select Department</option>
                            <option value="Sales">Sales</option>
                            <option value="Marketing">Marketing</option>
                            <option value="HR">HR</option>
                            <option value="IT">IT</option>
                            <option value="Logistics">Logistics</option>
                            <option value="Finance">Finance</option>
                            <option value="Customer Service">Customer Service</option>
                            <option value="Executive">Executive</option>
                            <option value="__custom__">Custom...</option>
                        </select>
                        {departmentCustom && (
                            <input
                                type="text"
                                className="w-full border p-2 rounded bg-gray-50 mt-2"
                                placeholder="Enter custom department"
                                value={department}
                                onChange={e => setDepartment(e.target.value)}
                                disabled={!can(FEATURE_PERMISSIONS.USER_MANAGE_ATTRIBUTES)}
                                autoFocus
                            />
                        )}
                    </div>
                </div>
                {/* Territories multi-select (admins only can edit; others see it grayed out) */}
                <div>
                    <label className="block text-sm font-medium mb-1">
                        Territories
                        <span className="text-gray-500 text-xs ml-2">(Optional — sales rep assignments)</span>
                    </label>
                    <div
                        className="relative"
                        ref={territoryDropdownRef}
                        style={!can(FEATURE_PERMISSIONS.USER_MANAGE_TERRITORIES) ? { opacity: 0.6 } : {}}
                    >
                        <button
                            type="button"
                            aria-expanded={territoryDropdownOpen}
                            aria-haspopup="listbox"
                            className="w-full text-left border p-2 rounded bg-gray-50 flex justify-between items-center"
                            onClick={() => {
                                if (!can(FEATURE_PERMISSIONS.USER_MANAGE_TERRITORIES)) return;
                                setTerritoryDropdownOpen(o => !o);
                            }}
                            disabled={!can(FEATURE_PERMISSIONS.USER_MANAGE_TERRITORIES)}
                            style={!can(FEATURE_PERMISSIONS.USER_MANAGE_TERRITORIES) ? { cursor: 'not-allowed' } : {}}
                        >
                            <span className="truncate">
                                {territories.length > 0 ? territories.join(', ') : 'Select Territories'}
                            </span>
                            <svg className="w-4 h-4 ml-2 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={territoryDropdownOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                            </svg>
                        </button>

                        {territoryDropdownOpen && can(FEATURE_PERMISSIONS.USER_MANAGE_TERRITORIES) && (
                            <div
                                role="listbox"
                                aria-multiselectable="true"
                                className="absolute z-40 mt-2 w-full bg-white border rounded shadow max-h-[60vh] overflow-auto p-2"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {availableTerritories.length === 0 && (
                                    <div className="text-xs text-gray-500 px-1 py-2">No territories yet. Create one below.</div>
                                )}
                                <div className="space-y-1">
                                    {availableTerritories.map(t => {
                                        const checked = territories.some(x => (x || '').toLowerCase() === t.toLowerCase());
                                        return (
                                            <label key={t} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => handleToggleTerritory(t)}
                                                    className="form-checkbox"
                                                    role="option"
                                                    aria-selected={checked}
                                                />
                                                <span className="text-sm">{t}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                                {/* Create new territory */}
                                <div className="mt-3 pt-3 border-t flex items-center gap-2">
                                    <input
                                        type="text"
                                        placeholder="New territory name"
                                        value={newTerritoryName}
                                        onChange={e => setNewTerritoryName(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleCreateTerritory();
                                            }
                                        }}
                                        maxLength={100}
                                        className="flex-1 border rounded px-2 py-1 text-sm"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleCreateTerritory}
                                        disabled={creatingTerritory || !newTerritoryName.trim()}
                                        className="px-3 py-1 bg-indigo-600 text-white rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {creatingTerritory ? 'Adding…' : 'Add'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    {/* Selected chips (also allow removal without opening the dropdown) */}
                    {territories.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {territories.map(t => (
                                <span
                                    key={t}
                                    className="inline-flex items-center bg-indigo-50 text-indigo-800 border border-indigo-200 text-xs rounded-full px-2 py-0.5"
                                >
                                    {t}
                                    {can(FEATURE_PERMISSIONS.USER_MANAGE_TERRITORIES) && (
                                        <button
                                            type="button"
                                            onClick={() => handleToggleTerritory(t)}
                                            className="ml-1 text-indigo-600 hover:text-indigo-900"
                                            aria-label={`Remove ${t}`}
                                        >
                                            ×
                                        </button>
                                    )}
                                </span>
                            ))}
                        </div>
                    )}
                    {!can(FEATURE_PERMISSIONS.USER_MANAGE_TERRITORIES) && (
                        <div className="text-xs text-gray-400 mt-1">Only admins can edit territory assignments.</div>
                    )}
                </div>
                {/* Role dropdown as the last field, full width */}
                <div>
                    <label className="block text-sm font-medium mb-1">Role<span className="text-red-600">*</span></label>
                    <select
                        className="w-full border p-2 rounded bg-gray-50"
                        value={role}
                        onChange={e => setRole(e.target.value)}
                        required
                        disabled={!can(FEATURE_PERMISSIONS.USER_MANAGE_PERMISSIONS)}
                        style={!can(FEATURE_PERMISSIONS.USER_MANAGE_PERMISSIONS)
                            ? { opacity: 0.6, cursor: 'not-allowed' }
                            : {}
                        }
                    >
                        <option value="" disabled={!!initial}>Select Role</option>
                        {availableRoles.map(r => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                    </select>
                </div>
                {/* Hide from Reports toggle */}
                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="hideFromReports"
                        checked={hideFromReports}
                        onChange={e => setHideFromReports(e.target.checked)}
                        disabled={!can(FEATURE_PERMISSIONS.USER_MANAGE_ATTRIBUTES)}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                    <label htmlFor="hideFromReports" className="text-sm font-medium text-gray-700">
                        Hide from Reports
                    </label>
                    <span className="text-xs text-gray-400 ml-1">(Excludes this user from all report charts and tables)</span>
                </div>
                {/* Manager dropdown - full width */}
                <div>
                    <label className="block text-sm font-medium mb-1">
                        Manager
                        <span className="text-gray-500 text-xs ml-2">(Optional)</span>
                    </label>
                    <select
                        className="w-full border p-2 rounded bg-gray-50"
                        value={managerId}
                        onChange={e => {
                            const selectedManager = availableManagers.find(m => m.id === e.target.value);
                            setManagerId(e.target.value);
                            setManagerName(selectedManager ? selectedManager.displayName : '');
                        }}
                        disabled={!can(FEATURE_PERMISSIONS.USER_MANAGE_ATTRIBUTES)}
                        style={!can(FEATURE_PERMISSIONS.USER_MANAGE_ATTRIBUTES)
                            ? { opacity: 0.6, cursor: 'not-allowed' }
                            : {}
                        }
                    >
                        <option value="">No Manager</option>
                        {availableManagers.map(m => (
                            <option key={m.id} value={m.id}>
                                {m.displayName || `${m.firstName} ${m.lastName}`} ({m.role})
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex items-center mt-6">
                    <div className="flex gap-2 flex-1">
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-4 py-2 bg-indigo-600 text-white rounded"
                        >
                            {saving ? 'Saving...' : (initial ? 'Save' : 'Add')}
                        </button>
                        <button
                            type="button"
                            onClick={() => onClose(false)}
                            className="px-4 py-2 bg-gray-200 rounded"
                        >
                            Cancel
                        </button>
                    </div>
                    {initial && user && initial.id !== user.uid && (
                        <button
                            type="button"
                            className={`px-4 py-2 text-white rounded ml-4 ${initial.role === 'disabled' ? 'bg-green-600' : 'bg-red-600'}`}
                            onClick={() => {
                                if (initial.role === 'disabled') {
                                    if (window.confirm('Reactivate this user? They will be able to log in again.')) {
                                        onClose({ reactivate: true, id: initial.id, email: initial.email, uid: initial.uid || initial.id });
                                    }
                                } else {
                                    if (
                                        window.confirm('Are you sure you want to deactivate this user? They will no longer be able to log in.') &&
                                        window.confirm('This will disable their account. Continue?')
                                    ) {
                                        onClose({ deactivate: true, id: initial.id, email: initial.email, uid: initial.uid || initial.id });
                                    }
                                }
                            }}
                        >
                            {initial.role === 'disabled' ? 'Reactivate' : 'Deactivate'}
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default UserModal;